import Big from 'big.js'
import { useState, useRef } from 'react'
import { isEmpty } from '../../../../utils/stringUtility'
import { FieldValidity } from '../../../Interfaces/FieldValidity'
import {
  convertTwoDecimalStringToBig,
  isValidInputDecimal,
} from '../../../../utils/bigJSHelper'
import { amountFromInput } from '../../../../utils/amountFromInput'
import getLocaleCurrencyForAmount from '../../../../utils/getLocaleCurrencyForAmount'
import { AmountSelection } from '../../utils/paymentModalSteps'

export interface UsePaymentAmountProps {
  total: Big
  defaultAmountOption: AmountSelection
}

export interface UsePaymentAmountReturn {
  amountOption: AmountSelection
  setAmountOption: (option: AmountSelection) => void
  amountOfPayment: string
  setAmountOfPayment: (amount: string) => void
  isAmountOfPaymentValid: FieldValidity
  handleAmountInputBlur: (
    event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void
  handleAmountOfPaymentChange: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void
  handleKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

export const usePaymentAmount = ({
  total,
  defaultAmountOption,
}: UsePaymentAmountProps): UsePaymentAmountReturn => {
  const [amountOption, setAmountOption] =
    useState<AmountSelection>(defaultAmountOption)
  const [amountOfPayment, setAmountOfPayment] = useState('')
  const [isAmountOfPaymentValid, setIsAmountOfPaymentValid] =
    useState<FieldValidity>({
      input: true,
      beforeMax: true,
    })

  const isDeletingKeyRef = useRef(false)

  const validatePaymentAmount = (amount: string) => {
    if (!amount) {
      setIsAmountOfPaymentValid({ input: true, beforeMax: true })
      return
    }
    const amountBigValue = convertTwoDecimalStringToBig(amount)
    setIsAmountOfPaymentValid({
      input: isEmpty(amount) || amountBigValue.gt(0),
      beforeMax: amountBigValue.lte(total),
    })
  }

  const handleAmountInputBlur = (
    event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const refinedValue = amountFromInput(event.target.value)
    setAmountOfPayment(getLocaleCurrencyForAmount(refinedValue, 'USD'))
    validatePaymentAmount(refinedValue)
  }

  const handleAmountOfPaymentChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value
    if (isDeletingKeyRef.current) {
      const refinedValue = amountFromInput(value)
      setAmountOfPayment(event.target.value)
      validatePaymentAmount(refinedValue)
    }

    if (!isValidInputDecimal(value)) return

    const refinedValue = amountFromInput(value)
    setAmountOfPayment(event.target.value)
    validatePaymentAmount(refinedValue)
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    const key = event.key
    if (key === 'Backspace' || key === 'Delete') {
      isDeletingKeyRef.current = true
    } else {
      isDeletingKeyRef.current = false
    }
  }

  return {
    amountOption,
    setAmountOption,
    amountOfPayment,
    setAmountOfPayment,
    isAmountOfPaymentValid,
    handleAmountInputBlur,
    handleAmountOfPaymentChange,
    handleKeyDown,
  }
}
